import { MetricToImperialConverter } from '../converters';
import { Allergens } from './ingredient';
import { MealIngredientData } from './mealIngredientData';

export enum Unit {
  Gram = 'g',
  Ounce = 'oz',
  Milliliter = 'ml',
  FluidOunce = 'fl.oz',
  TeaSpoon = 'tsp',
  Unit = 'UNIT(hide)',
}

export interface IMealIngredientsCount {
  quantity: number;
  unit: Unit;
  ingredientDisplayName: string;
}

// Will have Couchdb DB document
export class MealIngredient {
  public static getMealIngredientsCount(
    quantity: number,
    ingredient: MealIngredient,
    useImperialUnit: boolean,
    allergens: Allergens[],
  ): IMealIngredientsCount {
    const ingredientDisplayName = this.getDisplayNameOfIngredientOrReplacement(ingredient, allergens);
    if (ingredient.unit === Unit.Unit || ingredient.unit === Unit.TeaSpoon || !useImperialUnit) {
      return {
        quantity,
        unit: ingredient.unit,
        ingredientDisplayName,
      };
    }

    const imperialUnit = MetricToImperialConverter.convertMealIngredientUnitToImperial(ingredient.unit);
    const imperialQuantity = MetricToImperialConverter.convertAmount(ingredient.unit, imperialUnit, quantity);

    return {
      quantity: imperialQuantity,
      unit: imperialUnit,
      ingredientDisplayName,
    };
  }

  public static SumCalories(ingredients: MealIngredientData[]): number {
    return this.SumIngredient(ingredients, 'calories');
  }

  public static SumFat(ingredients: MealIngredientData[], useImperialUnit= false): number {
    return this.SumIngredient(ingredients, 'fat', useImperialUnit);
  }

  public static SumCarbs(ingredients: MealIngredientData[], useImperialUnit= false): number {
    return this.SumIngredient(ingredients, 'carbs', useImperialUnit);
  }

  public static SumProtein(ingredients: MealIngredientData[], useImperialUnit= false): number {
    return this.SumIngredient(ingredients, 'protein', useImperialUnit);
  }

  public static getDisplayNameOfIngredientOrReplacement(mealIngredient: MealIngredient, userAllergens: Allergens[]) {
    const replacement = this.getAllergenReplacement(mealIngredient,userAllergens);
    return replacement ?? mealIngredient.displayName;
  }

  private static getAllergenReplacement(
    mealIngredient: MealIngredient,
    userAllergens: Allergens[],
  ): string {
    const replacements = [];
    if (mealIngredient.eggReplacement && userAllergens.includes('Eggs')) {
      replacements.push(mealIngredient.eggReplacement);
    }
    if (mealIngredient.nutReplacement && userAllergens.includes('Nuts')) {
      replacements.push(mealIngredient.nutReplacement);
    }
    if (mealIngredient.glutenReplacement && userAllergens.includes('Gluten')) {
      replacements.push(mealIngredient.glutenReplacement);
    }
    if (mealIngredient.dairyReplacement && userAllergens.includes('Dairy')) {
      replacements.push(mealIngredient.dairyReplacement);
    }
    if (replacements.length > 1) {
      return mealIngredient.allAllergenReplacement;
    }
    return replacements[0];
  }

  private static SumIngredient(
    ingredientsData: MealIngredientData[],
    propertyName: string,
    useImperialUnit = false,
  ): number {
    if (ingredientsData === undefined || ingredientsData.length === 0) {
      return 0;
    }
    const ingredientRatio = ingredientsData.map((ingredientData) => {
      return ingredientData.ingredient[propertyName] * ingredientData.quantity;
    });
    const sum = ingredientRatio.reduce((prev: number, next: number) => {
      return prev + next;
    }, 0);
    if (useImperialUnit) {
      return MetricToImperialConverter.convertAmount(Unit.Gram, Unit.Ounce, sum);
    }
    return sum;
  }

  public '_id': string;
  public internalDescription: string;
  public protein: number;
  public fat: number;
  public carbs: number;
  public displayName: string;
  public calories: number;
  public unit: Unit;
  public category: string;
  public glutenReplacement: string;
  public eggReplacement: string;
  public nutReplacement: string;
  public dairyReplacement: string;
  public allAllergenReplacement: string;
}
